import { Sun, Moon, CloudSun, CloudMoon, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, Droplets, Wind, Thermometer, Umbrella, Sunrise, Sunset } from "lucide-react";

const WMO = [
  [[0], "Sereno", Sun, Moon, "text-amber-300"],
  [[1, 2], "Poco nuvoloso", CloudSun, CloudMoon, "text-amber-200"],
  [[3], "Coperto", Cloud, Cloud, "text-slate-300"],
  [[45, 48], "Nebbia", CloudFog, CloudFog, "text-slate-400"],
  [[51, 53, 55, 56, 57], "Pioviggine", CloudDrizzle, CloudDrizzle, "text-cyan-300"],
  [[61, 63, 65, 66, 67, 80, 81, 82], "Pioggia", CloudRain, CloudRain, "text-cyan-400"],
  [[71, 73, 75, 77, 85, 86], "Neve", CloudSnow, CloudSnow, "text-sky-100"],
  [[95, 96, 99], "Temporale", CloudLightning, CloudLightning, "text-violet-300"],
];
const info = (code, day = 1) => {
  const w = WMO.find(([codes]) => codes.includes(code)) || WMO[2];
  return { label: w[1], Icon: day ? w[2] : w[3], color: w[4] };
};
const DAYS = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const dayName = (iso, i) => (i === 0 ? "Oggi" : DAYS[new Date(`${iso}T12:00:00`).getDay()]);

const Stat = ({ Icon, label, value }) => (
  <div className="flex items-center gap-2 rounded-xl bg-white/[0.04] border border-cyan-500/10 px-3 py-2">
    <Icon size={15} className="text-cyan-300 shrink-0" />
    <div className="leading-tight"><p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p><p className="text-sm text-white">{value}</p></div>
  </div>
);

const Hours = ({ hours }) => (
  <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="weather-hours">
    {hours.map((h, i) => {
      const { Icon, color } = info(h.code, h.day);
      return (
        <div key={h.time + i} className="shrink-0 w-14 rounded-xl bg-white/[0.03] border border-cyan-500/10 py-2 flex flex-col items-center gap-1">
          <span className="text-[10px] font-mono text-slate-500">{i === 0 ? "Ora" : h.time}</span>
          <Icon size={18} className={color} />
          <span className="text-sm text-white">{h.temp}°</span>
          <span className="text-[10px] text-cyan-300/80 h-3">{h.rain >= 20 ? `${h.rain}%` : ""}</span>
        </div>
      );
    })}
  </div>
);

const Days = ({ days }) => {
  const lo = Math.min(...days.map((d) => d.min));
  const span = Math.max(1, Math.max(...days.map((d) => d.max)) - lo);
  return (
    <div className="space-y-1" data-testid="weather-days">
      {days.map((d, i) => {
        const { Icon, color, label } = info(d.code);
        return (
          <div key={d.date} className="grid grid-cols-[3rem_1.5rem_2.75rem_1fr_2rem] sm:grid-cols-[3.5rem_1.5rem_3rem_1fr_2.5rem] items-center gap-2 text-sm" title={label}>
            <span className="text-slate-300">{dayName(d.date, i)}</span>
            <Icon size={17} className={color} />
            <span className="text-[11px] text-cyan-300/80">{d.rain >= 20 ? `${d.rain}%` : ""}</span>
            <span className="relative h-1.5 rounded-full bg-white/10">
              <span className="absolute h-full rounded-full bg-gradient-to-r from-cyan-400 via-emerald-300 to-amber-300"
                style={{ left: `${((d.min - lo) / span) * 100}%`, right: `${100 - ((d.max - lo) / span) * 100}%` }} />
            </span>
            <span className="text-right text-white"><span className="text-slate-500 text-xs mr-1">{d.min}°</span>{d.max}°</span>
          </div>
        );
      })}
    </div>
  );
};

export const WeatherCard = ({ w }) => {
  const c = w.current;
  const { Icon, color, label } = info(c.code, c.day);
  const today = w.days[0] || {};
  return (
    <div data-testid="weather-card" className="hud-card relative overflow-hidden p-4 sm:p-5 mb-3 max-w-xl fade-up">
      <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gradient-to-br from-cyan-400/20 via-violet-500/10 to-transparent blur-2xl pointer-events-none" />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="hud-tag mb-1">Meteo · Open-Meteo</p>
          <h3 className="font-heading text-xl sm:text-2xl text-white truncate" data-testid="weather-place">{w.place}<span className="text-slate-500 text-sm ml-2">{w.country}</span></h3>
          <p className="text-sm text-slate-400">{label} · max {today.max}° / min {today.min}°</p>
        </div>
        <Icon size={58} strokeWidth={1.4} className={`${color} shrink-0 animate-[float_6s_ease-in-out_infinite] drop-shadow-[0_0_14px_currentColor]`} />
      </div>
      <p className="font-heading text-6xl sm:text-7xl font-bold text-aurora leading-none mt-2" data-testid="weather-temp">{c.temp}°</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
        <Stat Icon={Thermometer} label="Percepita" value={`${c.feels}°`} />
        <Stat Icon={Droplets} label="Umidità" value={`${c.humidity}%`} />
        <Stat Icon={Wind} label="Vento" value={`${c.wind} km/h`} />
        <Stat Icon={Umbrella} label="Pioggia" value={`${today.rain ?? 0}%`} />
      </div>
      <div className="mt-4"><Hours hours={w.hours} /></div>
      <div className="mt-4 pt-3 border-t border-cyan-500/10"><Days days={w.days} /></div>
      <div className="mt-3 flex gap-4 text-[11px] font-mono text-slate-500">
        <span className="flex items-center gap-1"><Sunrise size={13} /> {w.sunrise}</span>
        <span className="flex items-center gap-1"><Sunset size={13} /> {w.sunset}</span>
      </div>
    </div>
  );
};
